import { useState, useCallback, useRef, useMemo } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { handleAIError } from '../lib/aiErrorHandler';
import {
  AlertTriangle, Plus, X, Search, Loader2, ShieldAlert,
  ShieldCheck, ShieldQuestion, Info, Save, Download,
  User, Heart, Stethoscope, Cigarette, Baby, Leaf
} from 'lucide-react';
import { exportToPrintablePDF } from '../lib/exportUtils';
import { keyed } from '../lib/listKeys';

const SEVERITY_CONFIG = {
  high: { color: 'bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30', icon: ShieldAlert, label: 'High Risk' },
  moderate: { color: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30', icon: AlertTriangle, label: 'Moderate' },
  low: { color: 'bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30', icon: Info, label: 'Low Risk' },
  none: { color: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30', icon: ShieldCheck, label: 'Safe' },
};

const CONTRA_TYPE_CONFIG = {
  allergy: { color: 'bg-rose-500/10 text-rose-600 border-rose-500/30', label: 'Allergy' },
  condition: { color: 'bg-orange-500/10 text-orange-600 border-orange-500/30', label: 'Condition' },
  age: { color: 'bg-violet-500/10 text-violet-600 border-violet-500/30', label: 'Age' },
  pregnancy: { color: 'bg-pink-500/10 text-pink-600 border-pink-500/30', label: 'Pregnancy' },
  social: { color: 'bg-amber-500/10 text-amber-600 border-amber-500/30', label: 'Social' },
  environmental: { color: 'bg-teal-500/10 text-teal-600 border-teal-500/30', label: 'Environmental' },
};

const COMMON_MEDICATIONS = [
  'Paracetamol', 'Ibuprofen', 'Amoxicillin', 'Metformin', 'Amlodipine',
  'Omeprazole', 'Aspirin', 'Ciprofloxacin', 'Warfarin', 'Lisinopril',
  'Atorvastatin', 'Cetirizine', 'Azithromycin', 'Metoprolol', 'Losartan',
  'Pantoprazole', 'Diclofenac', 'Prednisone', 'Clopidogrel', 'Sertraline'
];

const AUTHORIZED_CLINICAL_ROLES = ['super_admin', 'admin', 'pharmacist'];

export default function DrugInteractionPage() {
  const { user } = useAuth();
  const [medications, setMedications] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Patient selection
  const [patientMode, setPatientMode] = useState('none'); // none, named, walkin
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const [showPatientSuggestions, setShowPatientSuggestions] = useState(false);
  const patientRef = useRef(null);

  // Walk-in profile
  const [walkIn, setWalkIn] = useState({
    age: '', gender: '', allergies: '', conditions: '',
    current_medications: '', social_history: '',
    environmental_factors: '', pregnancy_status: 'N/A'
  });

  // Memoised list of quick-add suggestions — filters out already-added meds once, not per render
  const visibleCommonMeds = useMemo(
    () => COMMON_MEDICATIONS.filter(m => !medications.includes(m)).slice(0, 8),
    [medications]
  );

  const addMedication = (med) => {
    const name = med.trim();
    if (!name || medications.includes(name)) return;
    setMedications(prev => [...prev, name]);
    setInputValue('');
    setShowSuggestions(false);
  };

  const removeMedication = (med) => {
    setMedications(prev => prev.filter(m => m !== med));
    setResults(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addMedication(inputValue);
    }
  };

  // Patient autocomplete
  const searchPatients = useCallback(async (q) => {
    if (!q || q.length < 2) { setPatientSuggestions([]); return; }
    try {
      const res = await api.get('/patients/autocomplete', { params: { q } });
      setPatientSuggestions(res.data.results || []);
      setShowPatientSuggestions(true);
    } catch { setPatientSuggestions([]); }
  }, []);

  const selectPatient = async (p) => {
    setShowPatientSuggestions(false);
    setPatientSearch(p.name);
    try {
      const res = await api.get(`/patients/${p.patient_id}`);
      setSelectedPatient(res.data);
    } catch {
      toast.error('Failed to load patient profile');
    }
  };

  const checkInteractions = async () => {
    if (medications.length < 1) {
      toast.error('Add at least 1 medication');
      return;
    }
    setLoading(true);
    setResults(null);
    try {
      const body = { medications };
      if (patientMode === 'named' && selectedPatient) {
        body.patient_id = selectedPatient.patient_id;
      } else if (patientMode === 'walkin') {
        body.walk_in_profile = walkIn;
      }
      const res = await api.post('/ai/drug-interaction', body);
      setResults(res.data);
    } catch (err) {
      handleAIError(err, 'Failed to check interactions');
    } finally {
      setLoading(false);
    }
  };

  const filteredSuggestions = COMMON_MEDICATIONS.filter(
    m => m.toLowerCase().includes(inputValue.toLowerCase()) && !medications.includes(m)
  ).slice(0, 6);

  const overallConfig = results ? (SEVERITY_CONFIG[results.overall_risk] || SEVERITY_CONFIG.none) : null;

  const handleSaveReport = async () => {
    try {
      await api.post('/ai/drug-interaction/save', { medications, result: results });
      toast.success('Report saved');
    } catch { toast.error('Failed to save'); }
  };

  const handleExportPDF = () => {
    if (!results) return;
    const headers = ['Drug Pair', 'Severity', 'Description', 'Recommendation'];
    const rows = (results.interactions || []).map(i => [
      i.drug_pair?.join(' + ') || '', i.severity || '', i.description || '', i.recommendation || '',
    ]);
    if (results.contraindications?.length) {
      rows.push(['', '', '', '']);
      rows.push(['CONTRAINDICATIONS', '', '', '']);
      results.contraindications.forEach(c => {
        rows.push([c.drug || '', `${c.severity} (${c.type})`, c.description || '', c.recommendation || '']);
      });
    }
    exportToPrintablePDF({
      title: 'Drug Interaction & Contraindication Report',
      subtitle: `Medications: ${medications.join(', ')} | Patient: ${selectedPatient?.name || patientMode === 'walkin' ? 'Walk-in' : 'Not specified'} | Risk: ${results.overall_risk?.toUpperCase() || 'Unknown'}`,
      summaryCards: [
        { label: 'Medications', value: medications.length },
        { label: 'Interactions', value: results.interactions?.length || 0 },
        { label: 'Contraindications', value: results.contraindications?.length || 0 },
        { label: 'Overall Risk', value: results.overall_risk?.toUpperCase() || 'Unknown' },
      ],
      headers, rows,
    });
  };

  if (!AUTHORIZED_CLINICAL_ROLES.includes(user?.role)) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center space-y-3" data-testid="drug-interaction-unauthorized">
        <ShieldAlert className="h-10 w-10 text-muted-foreground mx-auto" />
        <p className="text-lg font-medium">Please talk to the pharmacist on duty.</p>
        <p className="text-sm text-muted-foreground">Drug interaction checks are available to pharmacists and owners only.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="drug-interaction-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Drug Interaction & Contraindication Checker
        </h1>
        <p className="text-sm text-muted-foreground mt-1">AI-powered medication safety with patient-specific analysis</p>
      </div>

      {/* Patient Profile Section */}
      <Card data-testid="patient-profile-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <User className="h-4 w-4 text-primary" /> Patient Profile
          </CardTitle>
          <CardDescription>Select a patient for personalized contraindication analysis</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button variant={patientMode === 'none' ? 'default' : 'outline'} size="sm" onClick={() => { setPatientMode('none'); setSelectedPatient(null); }} data-testid="mode-none">No Patient</Button>
            <Button variant={patientMode === 'named' ? 'default' : 'outline'} size="sm" className="gap-1.5" onClick={() => setPatientMode('named')} data-testid="mode-named">
              <Heart className="h-3.5 w-3.5" /> Named Patient
            </Button>
            <Button variant={patientMode === 'walkin' ? 'default' : 'outline'} size="sm" className="gap-1.5" onClick={() => setPatientMode('walkin')} data-testid="mode-walkin">
              <User className="h-3.5 w-3.5" /> Walk-in Profile
            </Button>
          </div>

          {patientMode === 'named' && (
            <div className="space-y-3">
              <div className="relative" ref={patientRef}>
                <Input
                  placeholder="Search patient by name or phone..."
                  value={patientSearch}
                  onChange={(e) => { setPatientSearch(e.target.value); searchPatients(e.target.value); }}
                  onFocus={() => { if (patientSuggestions.length) setShowPatientSuggestions(true); }}
                  onBlur={() => setTimeout(() => setShowPatientSuggestions(false), 200)}
                  data-testid="patient-search-input"
                />
                {showPatientSuggestions && patientSuggestions.length > 0 && (
                  <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-popover border rounded-lg shadow-lg max-h-40 overflow-y-auto" data-testid="patient-search-results">
                    {patientSuggestions.map(p => (
                      <button key={p.patient_id} className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between"
                        onMouseDown={() => selectPatient(p)} data-testid={`pick-patient-${p.patient_id}`}>
                        <span className="font-medium">{p.name}</span>
                        <span className="text-xs text-muted-foreground">{p.phone || ''}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedPatient && (
                <div className="p-3 bg-muted/50 rounded-lg space-y-2" data-testid="selected-patient-info">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold flex items-center gap-1.5"><Heart className="h-3.5 w-3.5 text-primary" /> {selectedPatient.name}</p>
                    <Button variant="ghost" size="sm" className="h-6 text-xs text-destructive" onClick={() => { setSelectedPatient(null); setPatientSearch(''); }}>Clear</Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-muted-foreground">
                    {selectedPatient.gender && <span>Gender: {selectedPatient.gender}</span>}
                    {selectedPatient.date_of_birth && <span>DOB: {selectedPatient.date_of_birth}</span>}
                    {selectedPatient.phone && <span>Ph: {selectedPatient.phone}</span>}
                    {selectedPatient.category && <span>Cat: {selectedPatient.category}</span>}
                  </div>
                  {selectedPatient.allergies?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      <span className="text-xs text-destructive font-medium">Allergies:</span>
                      {keyed(selectedPatient.allergies).map(({ item: a, key }) => <Badge key={key} variant="destructive" className="text-[9px]">{a}</Badge>)}
                    </div>
                  )}
                  {selectedPatient.notes && <p className="text-[11px] text-muted-foreground">{selectedPatient.notes}</p>}
                </div>
              )}
            </div>
          )}

          {patientMode === 'walkin' && (
            <div className="grid grid-cols-2 gap-3" data-testid="walkin-form">
              <div className="space-y-1.5">
                <Label className="text-xs">Age</Label>
                <Input placeholder="e.g. 45" value={walkIn.age} onChange={e => setWalkIn(p => ({ ...p, age: e.target.value }))} data-testid="walkin-age" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Gender</Label>
                <Select value={walkIn.gender} onValueChange={v => setWalkIn(p => ({ ...p, gender: v }))}>
                  <SelectTrigger data-testid="walkin-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs flex items-center gap-1"><ShieldAlert className="h-3 w-3 text-destructive" /> Allergies</Label>
                <Input placeholder="e.g. Penicillin, Sulfa, Aspirin" value={walkIn.allergies} onChange={e => setWalkIn(p => ({ ...p, allergies: e.target.value }))} data-testid="walkin-allergies" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs flex items-center gap-1"><Stethoscope className="h-3 w-3" /> Medical Conditions</Label>
                <Input placeholder="e.g. Diabetes, Hypertension, Asthma, Renal impairment" value={walkIn.conditions} onChange={e => setWalkIn(p => ({ ...p, conditions: e.target.value }))} data-testid="walkin-conditions" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">Current Medications</Label>
                <Input placeholder="e.g. Metformin 500mg, Lisinopril 10mg" value={walkIn.current_medications} onChange={e => setWalkIn(p => ({ ...p, current_medications: e.target.value }))} data-testid="walkin-current-meds" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1"><Cigarette className="h-3 w-3" /> Social History</Label>
                <Input placeholder="Smoking, alcohol, substance use" value={walkIn.social_history} onChange={e => setWalkIn(p => ({ ...p, social_history: e.target.value }))} data-testid="walkin-social" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1"><Leaf className="h-3 w-3" /> Environmental</Label>
                <Input placeholder="Occupation, exposures" value={walkIn.environmental_factors} onChange={e => setWalkIn(p => ({ ...p, environmental_factors: e.target.value }))} data-testid="walkin-env" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1"><Baby className="h-3 w-3" /> Pregnancy / Lactation</Label>
                <Select value={walkIn.pregnancy_status} onValueChange={v => setWalkIn(p => ({ ...p, pregnancy_status: v }))}>
                  <SelectTrigger data-testid="walkin-pregnancy"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="N/A">N/A</SelectItem>
                    <SelectItem value="pregnant">Pregnant</SelectItem>
                    <SelectItem value="lactating">Lactating</SelectItem>
                    <SelectItem value="planning">Planning pregnancy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Medications Input */}
      <Card data-testid="drug-input-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Search className="h-4 w-4 text-primary" /> Medications
          </CardTitle>
          <CardDescription>Add medications to check for interactions and contraindications</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {medications.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {medications.map(med => (
                <Badge key={med} variant="secondary" className="text-sm py-1 pl-3 pr-1 gap-1.5" data-testid={`med-badge-${med}`}>
                  {med}
                  <button onClick={() => removeMedication(med)} className="h-5 w-5 rounded-full hover:bg-destructive/20 flex items-center justify-center" data-testid={`remove-med-${med}`}>
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          <div className="relative">
            <Input placeholder="Type medication name..." value={inputValue}
              onChange={(e) => { setInputValue(e.target.value); setShowSuggestions(true); }}
              onKeyDown={handleKeyDown}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="h-11" data-testid="drug-search-input" />
            {showSuggestions && inputValue && filteredSuggestions.length > 0 && (
              <div className="absolute top-full mt-1 left-0 right-0 bg-popover border rounded-lg shadow-lg z-50 overflow-hidden" data-testid="drug-suggestions">
                {filteredSuggestions.map(med => (
                  <button key={med} className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                    onMouseDown={() => addMedication(med)} data-testid={`suggest-${med}`}>{med}</button>
                ))}
              </div>
            )}
          </div>

          <div>
            <p className="text-xs text-muted-foreground mb-2 font-medium">Quick add:</p>
            <div className="flex flex-wrap gap-1.5">
              {visibleCommonMeds.map(med => (
                <Button key={med} variant="outline" size="sm" className="h-7 text-xs" onClick={() => addMedication(med)} data-testid={`quick-add-${med}`}>
                  <Plus className="h-3 w-3 mr-1" /> {med}
                </Button>
              ))}
            </div>
          </div>

          <Separator />

          <Button className="w-full h-11 font-semibold gap-2" onClick={checkInteractions}
            disabled={medications.length < 1 || loading} data-testid="check-interactions-btn">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing...</> :
              <><ShieldAlert className="h-4 w-4" /> Check Interactions & Contraindications ({medications.length})</>}
          </Button>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <Card className="animate-pulse">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-3" />
            <p className="text-sm text-muted-foreground">Analyzing interactions, contraindications, and patient-specific risks...</p>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {results && !loading && (
        <div className="space-y-4 animate-fade-in-up" data-testid="interaction-results">
          {/* Overall Risk */}
          {overallConfig && (
            <Alert className={`${overallConfig.color} border`} data-testid="overall-risk-alert">
              <overallConfig.icon className="h-5 w-5" />
              <AlertTitle className="font-semibold text-base">Overall Risk: {overallConfig.label}</AlertTitle>
              <AlertDescription className="mt-1 text-sm">{results.summary}</AlertDescription>
            </Alert>
          )}

          {/* Patient Warnings */}
          {results.patient_warnings?.length > 0 && (
            <Card className="border-amber-500/30" data-testid="patient-warnings-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2 text-amber-600">
                  <User className="h-4 w-4" /> Patient-Specific Warnings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {keyed(results.patient_warnings).map(({ item: w, key }) => (
                    <li key={key} className="text-sm flex items-start gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0" />{w}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Contraindications */}
          {results.contraindications?.length > 0 && (
            <Card data-testid="contraindications-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <ShieldAlert className="h-4 w-4 text-destructive" /> Contraindications ({results.contraindications.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {keyed(results.contraindications, c => `${c.drug}-${c.type}`).map(({ item: c, key }, i) => {
                    const sevConfig = SEVERITY_CONFIG[c.severity] || SEVERITY_CONFIG.low;
                    const typeConfig = CONTRA_TYPE_CONFIG[c.type] || { color: '', label: c.type };
                    return (
                      <div key={key} className={`p-3 rounded-lg border ${sevConfig.color}`} data-testid={`contraindication-${i}`}>
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <span className="text-sm font-semibold">{c.drug}</span>
                          <Badge variant="outline" className={`text-[9px] ${sevConfig.color}`}>{sevConfig.label}</Badge>
                          <Badge variant="outline" className={`text-[9px] ${typeConfig.color}`}>{typeConfig.label}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{c.description}</p>
                        {c.recommendation && <p className="text-sm font-medium mt-1">Rec: {c.recommendation}</p>}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Warnings */}
          {results.warnings?.length > 0 && (
            <Card className="border-destructive/30" data-testid="warnings-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2 text-destructive">
                  <AlertTriangle className="h-4 w-4" /> Critical Warnings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1.5">
                  {keyed(results.warnings).map(({ item: w, key }) => (
                    <li key={key} className="text-sm flex items-start gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-destructive mt-1.5 shrink-0" />{w}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Interaction Details */}
          {results.interactions?.length > 0 && (
            <Card data-testid="interactions-detail-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">Drug-Drug Interactions ({results.interactions.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {keyed(results.interactions, interaction => (interaction.drugs || interaction.drug_pair || []).join('+') || interaction.description).map(({ item: interaction, key }, i) => {
                    const config = SEVERITY_CONFIG[interaction.severity] || SEVERITY_CONFIG.low;
                    return (
                      <div key={key} className="p-3 rounded-lg bg-muted/50 space-y-1.5" data-testid={`interaction-${i}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <config.icon className="h-4 w-4 shrink-0" />
                            <span className="text-sm font-semibold">{interaction.drug_pair?.join(' + ')}</span>
                          </div>
                          <Badge className={`${config.color} border text-xs`}>{config.label}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{interaction.description}</p>
                        {interaction.recommendation && <p className="text-sm font-medium">Rec: {interaction.recommendation}</p>}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Safe Medications */}
          {results.safe_medications?.length > 0 && (
            <Card className="border-emerald-500/20" data-testid="safe-meds-card">
              <CardContent className="p-4">
                <p className="text-sm font-semibold flex items-center gap-2 mb-2"><ShieldCheck className="h-4 w-4 text-emerald-500" /> Safe for this Patient</p>
                <div className="flex flex-wrap gap-1.5">
                  {keyed(results.safe_medications).map(({ item: m, key }) => (
                    <Badge key={key} variant="outline" className="text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/30">{m}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {results.interactions?.length === 0 && results.contraindications?.length === 0 && (
            <Card>
              <CardContent className="p-8 text-center">
                <ShieldCheck className="h-10 w-10 mx-auto mb-3 text-emerald-500" />
                <p className="font-semibold">No Concerns Found</p>
                <p className="text-sm text-muted-foreground mt-1">No significant interactions or contraindications detected.</p>
              </CardContent>
            </Card>
          )}

          <p className="text-xs text-muted-foreground text-center">AI-powered analysis. Always consult a qualified pharmacist for final verification.</p>

          <div className="flex justify-center gap-3">
            <Button variant="outline" className="gap-1.5" onClick={handleSaveReport} data-testid="save-interaction-btn">
              <Save className="h-4 w-4" /> Save Report
            </Button>
            <Button variant="outline" className="gap-1.5" onClick={handleExportPDF} data-testid="export-interaction-btn">
              <Download className="h-4 w-4" /> Export PDF
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
